import { useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import Auth from '../../../../api/auth';
import { FormikSelect } from '../../../../components/Formik';
import useApiQuery from '../../../../hooks/useApiQuery';

import type { APIHookArg, ApiResponse } from '@/types';
import type { ComputeOrderValues, Product } from '@/types/productTypes';

interface Project {
  _id: string;
  name: string;
}

interface UserState {
  productIds?: string[];
}

interface ProjectSelectProps {
  setFilteredProducts: (products: Product[]) => void;
}

interface FormikSelectItem {
  id?: string | undefined;
  value?: string | undefined;
  label?: string | undefined;
}

const ProjectSelect = ({ setFilteredProducts }: ProjectSelectProps): React.JSX.Element => {
  const intl = useIntl();
  const { makeApiCall: fetchProjects } = useApiQuery(
    Auth.fetchV2Projects as unknown as APIHookArg<ApiResponse<Project>>
  );
  const { makeApiCall: fetchProducts } = useApiQuery(
    Auth.fetchV2Products as unknown as APIHookArg<ApiResponse<Product>>
  );
  const userData = useSelector((state: { user: UserState }) => state.user);
  const [projects, setProjects] = useState<Project[]>([]);
  const {
    values: { osProjectName },
    setValues,
  } = useFormikContext<ComputeOrderValues>();

  useEffect(() => {
    async function getProjectsAndProducts(): Promise<void> {
      const projectsData = await fetchProjects({}, false);
      const productsData = await fetchProducts({ types: ['compute'], enrich: true });

      const filteredProducts = (productsData?.docs ?? []).filter(
        product => product.approved && userData.productIds?.includes(product._id)
      );
      const projectIds = new Set(filteredProducts.map(product => product.projectId));

      const filteredProjects = (projectsData?.docs ?? []).filter(project =>
        projectIds.has(project._id)
      );

      setFilteredProducts(filteredProducts);
      setProjects(filteredProjects);

      // Set button helper text if no projects available
      if (filteredProjects.length === 0) {
        setValues(prev => ({
          ...prev,
          buttonHelperText: intl.formatMessage({ id: 'noProjectsAvailable' }),
        }));
      }
    }
    getProjectsAndProducts();
  }, [userData, setFilteredProducts, fetchProjects, fetchProducts, setValues, intl]);

  return (
    <FormikSelect
      name='projectId'
      label={intl.formatMessage({ id: 'project' })}
      helperText={projects.length === 0 ? intl.formatMessage({ id: 'noProjectsAvailable' }) : ''}
      menuItems={projects.map(project => ({
        value: project._id,
        label: project.name,
      }))}
      onChange={(selected: FormikSelectItem) => {
        setValues({
          osProjectName,
          projectId: selected.value ?? '',
          productId: '',
          cloudName: '',
          cpuCores: '',
          ram: '',
          diskQuota: '',
          floatingIps: '',
          internetConnectivity: false,
          DTHConnectivity: false,
          DTHRedRouterConnectivity: false,
          juiceGroup: '',
          unusedQuota: {
            compute: {
              cores: 0,
              ram: 0,
            },
            volume: {
              gigabytes: 0,
            },
            network: {
              floatingIps: 0,
            },
          },
          externalNetworks: [],
          buttonHelperText: '',
        });
      }}
    />
  );
};

export default ProjectSelect;
